/**
 * ProjectTimelineTab - Onglet timeline dans le détail projet
 * Sprint 3.3 - US-3.3.1: Suivi Avancement Visuel
 *
 * Design System: Dashboard (text-gray-900, bg-white/70)
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  CheckCircle2,
  Circle,
  PlayCircle,
  Calendar,
  Camera,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
} from 'lucide-react';
import { useProjectTimeline } from '../hooks/useClientPortal';
import type { ClientPhase, ClientPhoto, ClientPhaseStatus } from '../types/client-portal.types';

interface ProjectTimelineTabProps {
  projectId: string;
}

// Icône de statut selon l'avancement
function getStatusIcon(status: ClientPhaseStatus) {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    case 'IN_PROGRESS':
      return <PlayCircle className="w-5 h-5 text-teal-600" />;
    default:
      return <Circle className="w-5 h-5 text-gray-300" />;
  }
}

// Couleur de la barre de progression
function getProgressColor(status: ClientPhaseStatus): string {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-500';
    case 'IN_PROGRESS':
      return 'bg-teal-500';
    default:
      return 'bg-gray-200';
  }
}

// Formater la date
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Formater la taille du fichier
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function ProjectTimelineTab({ projectId }: ProjectTimelineTabProps) {
  const { t } = useTranslation();
  const { data: timeline, isLoading, error } = useProjectTimeline(projectId);
  const [selectedPhoto, setSelectedPhoto] = useState<ClientPhoto | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Navigation dans la lightbox
  const handlePrevPhoto = useCallback(() => {
    if (timeline && timeline.recentPhotos.length > 0) {
      setLightboxIndex((prev) =>
        prev === 0 ? timeline.recentPhotos.length - 1 : prev - 1
      );
      setSelectedPhoto(timeline.recentPhotos[lightboxIndex === 0 ? timeline.recentPhotos.length - 1 : lightboxIndex - 1]);
    }
  }, [timeline, lightboxIndex]);

  const handleNextPhoto = useCallback(() => {
    if (timeline && timeline.recentPhotos.length > 0) {
      setLightboxIndex((prev) =>
        prev === timeline.recentPhotos.length - 1 ? 0 : prev + 1
      );
      setSelectedPhoto(timeline.recentPhotos[lightboxIndex === timeline.recentPhotos.length - 1 ? 0 : lightboxIndex + 1]);
    }
  }, [timeline, lightboxIndex]);

  // Ouvrir la lightbox
  const openLightbox = useCallback((photo: ClientPhoto, index: number) => {
    setSelectedPhoto(photo);
    setLightboxIndex(index);
  }, []);

  // Télécharger une photo
  const handleDownload = useCallback((photo: ClientPhoto) => {
    window.open(photo.fileUrl, '_blank');
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
        <p className="text-red-600">{t('common.clientPortal.timeline.errorLoading')}</p>
      </div>
    );
  }

  if (!timeline) {
    return null;
  }

  const hasPhases = timeline.phases.length > 0;
  const hasPhotos = timeline.recentPhotos.length > 0;

  return (
    <div className="space-y-6">
      {/* Progression globale */}
      <div className="bg-white/50 rounded-xl border border-white/60 p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">{t('common.clientPortal.timeline.globalProgress')}</h3>
          <span className="text-lg font-bold text-teal-600">
            {Math.round(timeline.progressPercent)}%
          </span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-teal-500 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(timeline.progressPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Timeline des phases */}
      <div className="bg-white/50 rounded-xl border border-white/60 overflow-hidden">
        <div className="bg-white/70 px-4 py-3 border-b border-white/60">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('common.clientPortal.timeline.workTimeline')}</h3>
            <span className="text-sm text-gray-500">({timeline.phases.length} {t('common.clientPortal.timeline.lots')})</span>
          </div>
        </div>

        {!hasPhases ? (
          <div className="p-8 text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('common.clientPortal.timeline.noPhases')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {timeline.phases.map((phase, index) => (
              <PhaseRow key={phase.id} phase={phase} isLast={index === timeline.phases.length - 1} />
            ))}
          </div>
        )}
      </div>

      {/* Galerie photos */}
      <div className="bg-white/50 rounded-xl border border-white/60 overflow-hidden">
        <div className="bg-white/70 px-4 py-3 border-b border-white/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-teal-600" />
              <h3 className="font-semibold text-gray-900">{t('common.clientPortal.timeline.projectPhotos')}</h3>
              <span className="text-sm text-gray-500">({timeline.totalPhotos} {t('common.clientPortal.timeline.photos')})</span>
            </div>
          </div>
        </div>

        {!hasPhotos ? (
          <div className="p-8 text-center">
            <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('common.clientPortal.timeline.noPhotos')}</p>
          </div>
        ) : (
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {timeline.recentPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="relative group cursor-pointer aspect-square rounded-lg overflow-hidden bg-gray-100"
                  onClick={() => openLightbox(photo, index)}
                >
                  <img
                    src={photo.fileUrl}
                    alt={photo.fileName}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </div>
              ))}
            </div>
            {timeline.totalPhotos > timeline.recentPhotos.length && (
              <p className="text-center text-sm text-gray-500 mt-4">
                {t('common.clientPortal.timeline.photosDisplayed', { displayed: timeline.recentPhotos.length, total: timeline.totalPhotos })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPhoto(null);
            }}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {timeline.recentPhotos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevPhoto();
                }}
                className="absolute left-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextPhoto();
                }}
                className="absolute right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          <div className="max-w-4xl max-h-[80vh] p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.fileUrl}
              alt={selectedPhoto.fileName}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-white text-center">
              <p className="font-medium">{selectedPhoto.fileName}</p>
              <p className="text-sm text-white/70">
                {formatDate(selectedPhoto.uploadedAt)}
                {selectedPhoto.uploadedByName && ` • ${selectedPhoto.uploadedByName}`}
                {' • '}{formatFileSize(selectedPhoto.fileSize)}
              </p>
              {selectedPhoto.description && (
                <p className="text-sm text-white/80 mt-2">{selectedPhoto.description}</p>
              )}
              <button
                onClick={() => handleDownload(selectedPhoto)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                {t('common.clientPortal.timeline.download')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant ligne de phase
function PhaseRow({ phase, isLast }: { phase: ClientPhase; isLast: boolean }) {
  return (
    <div className="flex items-start gap-4 p-4">
      {/* Timeline verticale */}
      <div className="flex flex-col items-center">
        {getStatusIcon(phase.status)}
        {!isLast && (
          <div className={`w-0.5 h-full min-h-[40px] mt-2 ${
            phase.status === 'COMPLETED' ? 'bg-green-200' : 'bg-gray-200'
          }`} />
        )}
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">{phase.code}</span>
              <h4 className="font-medium text-gray-900">{phase.name}</h4>
            </div>
            {phase.description && (
              <p className="text-sm text-gray-500 mt-1">{phase.description}</p>
            )}
            {(phase.startDate || phase.endDate) && (
              <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                <Calendar className="w-3 h-3" />
                <span>
                  {phase.startDate ? formatDate(phase.startDate) : '-'}
                  {' → '}
                  {phase.endDate ? formatDate(phase.endDate) : '-'}
                </span>
              </div>
            )}
          </div>

          {/* Progression */}
          <div className="text-right shrink-0">
            <span className={`text-sm font-bold ${
              phase.status === 'COMPLETED' ? 'text-green-600' :
              phase.status === 'IN_PROGRESS' ? 'text-teal-600' : 'text-gray-400'
            }`}>
              {Math.round(phase.progressPercent)}%
            </span>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-3">
          <div
            className={`h-full ${getProgressColor(phase.status)} rounded-full transition-all duration-500`}
            style={{ width: `${Math.min(phase.progressPercent, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default ProjectTimelineTab;
